import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import logoImage from "../images/Zahira OBA Logo.png";

const navClass = ({ isActive }: { isActive: boolean }) =>
  [
    "text-sm font-semibold tracking-wide uppercase transition-colors",
    isActive ? "text-brand-maroon" : "text-brand-blue hover:text-brand-maroon",
  ].join(" ");

function alumniSectionActive(pathname: string) {
  const prefixes = ["/alumni/branches", "/alumni/batches", "/alumni/associations"];
  return prefixes.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`)
  );
}

function obaAboutSectionActive(pathname: string) {
  return (
    pathname === "/about" ||
    pathname === "/pillars" ||
    pathname.startsWith("/about/")
  );
}

export function Header() {
  const location = useLocation();
  const [alumniOpen, setAlumniOpen] = useState(false);
  const [newsOpen, setNewsOpen] = useState(false);
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const alumniWrapRef = useRef<HTMLDivElement>(null);
  const newsWrapRef = useRef<HTMLDivElement>(null);
  const projectsWrapRef = useRef<HTMLDivElement>(null);
  const aboutWrapRef = useRef<HTMLDivElement>(null);

  const alumniActive = alumniSectionActive(location.pathname);
  const newsActive =
    location.pathname === "/news" || location.pathname.startsWith("/news/");
  const projectsActive =
    location.pathname === "/projects" ||
    location.pathname.startsWith("/projects/");
  const obaAboutActive = obaAboutSectionActive(location.pathname);

  useEffect(() => {
    function onDocMouseDown(e: MouseEvent) {
      const t = e.target as Node;
      if (!alumniWrapRef.current?.contains(t)) setAlumniOpen(false);
      if (!newsWrapRef.current?.contains(t)) setNewsOpen(false);
      if (!projectsWrapRef.current?.contains(t)) setProjectsOpen(false);
      if (!aboutWrapRef.current?.contains(t)) setAboutOpen(false);
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  useEffect(() => {
    setAlumniOpen(false);
    setNewsOpen(false);
    setProjectsOpen(false);
    setAboutOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!alumniOpen && !newsOpen && !projectsOpen && !aboutOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setAlumniOpen(false);
        setNewsOpen(false);
        setProjectsOpen(false);
        setAboutOpen(false);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [alumniOpen, newsOpen, projectsOpen, aboutOpen]);

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-brand-gray/80 bg-white/95 backdrop-blur">
      <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-3 md:flex-row md:items-center md:justify-between">
        <Link
          to="/"
          className="group flex items-center gap-3 md:gap-4"
        >
          <img
            src={logoImage}
            alt="Zahira College Mawanella Old Boys’ Association"
            className="h-11 w-auto shrink-0 object-contain md:h-[3.25rem]"
          />
          <div>
            <p className="font-display text-lg font-bold leading-tight text-brand-blue group-hover:text-brand-maroon md:text-xl">
              Old Boys’ Association
            </p>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-maroon md:text-sm">
              Zahira College Mawanella
            </p>
          </div>
        </Link>
        <nav className="flex flex-wrap items-center gap-x-5 gap-y-2 md:gap-x-6">
          <NavLink to="/" className={navClass} end>
            Home
          </NavLink>

          <div className="relative" ref={alumniWrapRef}>
            <button
              type="button"
              aria-expanded={alumniOpen}
              aria-haspopup="true"
              onClick={() => setAlumniOpen((o) => !o)}
              className={[
                "inline-flex items-center gap-1 text-sm font-semibold tracking-wide uppercase transition-colors",
                alumniActive || alumniOpen
                  ? "text-brand-maroon"
                  : "text-brand-blue hover:text-brand-maroon",
              ].join(" ")}
            >
              Alumni
              <span className="text-xs opacity-80" aria-hidden>
                {alumniOpen ? "▴" : "▾"}
              </span>
            </button>
            {alumniOpen ? (
              <div
                className="absolute left-0 top-full z-50 mt-1 min-w-[11rem] rounded-lg border border-brand-gray bg-white py-1 shadow-lg"
                role="menu"
              >
                <Link
                  to="/alumni/branches"
                  role="menuitem"
                  className="block px-4 py-2 text-sm font-semibold text-brand-blue hover:bg-brand-gray/50 hover:text-brand-maroon"
                >
                  Branch
                </Link>
                <Link
                  to="/alumni/batches"
                  role="menuitem"
                  className="block px-4 py-2 text-sm font-semibold text-brand-blue hover:bg-brand-gray/50 hover:text-brand-maroon"
                >
                  Batch
                </Link>
                <Link
                  to="/alumni/associations"
                  role="menuitem"
                  className="block px-4 py-2 text-sm font-semibold text-brand-blue hover:bg-brand-gray/50 hover:text-brand-maroon"
                >
                  Association
                </Link>
              </div>
            ) : null}
          </div>

          <div className="relative" ref={aboutWrapRef}>
            <button
              type="button"
              aria-expanded={aboutOpen}
              aria-haspopup="true"
              onClick={() => setAboutOpen((o) => !o)}
              className={[
                "inline-flex items-center gap-1 text-sm font-semibold tracking-wide uppercase transition-colors",
                obaAboutActive || aboutOpen
                  ? "text-brand-maroon"
                  : "text-brand-blue hover:text-brand-maroon",
              ].join(" ")}
            >
              About OBA
              <span className="text-xs opacity-80" aria-hidden>
                {aboutOpen ? "▴" : "▾"}
              </span>
            </button>
            {aboutOpen ? (
              <div
                className="absolute left-0 top-full z-50 mt-1 min-w-[12rem] rounded-lg border border-brand-gray bg-white py-1 shadow-lg"
                role="menu"
              >
                <p className="px-4 pb-1 pt-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-maroon/90">
                  OBA
                </p>
                <Link
                  to="/about"
                  role="menuitem"
                  className="block px-4 py-2 text-sm font-semibold text-brand-blue hover:bg-brand-gray/50 hover:text-brand-maroon"
                >
                  About
                </Link>
                <Link
                  to="/pillars"
                  role="menuitem"
                  className="block px-4 py-2 text-sm font-semibold text-brand-blue hover:bg-brand-gray/50 hover:text-brand-maroon"
                >
                  Pillars
                </Link>
              </div>
            ) : null}
          </div>

          <div className="relative" ref={projectsWrapRef}>
            <button
              type="button"
              aria-expanded={projectsOpen}
              aria-haspopup="true"
              onClick={() => setProjectsOpen((o) => !o)}
              className={[
                "inline-flex items-center gap-1 text-sm font-semibold tracking-wide uppercase transition-colors",
                projectsActive || projectsOpen
                  ? "text-brand-maroon"
                  : "text-brand-blue hover:text-brand-maroon",
              ].join(" ")}
            >
              Projects
              <span className="text-xs opacity-80" aria-hidden>
                {projectsOpen ? "▴" : "▾"}
              </span>
            </button>
            {projectsOpen ? (
              <div
                className="absolute left-0 top-full z-50 mt-1 min-w-[14rem] rounded-lg border border-brand-gray bg-white py-1 shadow-lg"
                role="menu"
              >
                <Link
                  to="/projects"
                  role="menuitem"
                  className="block px-4 py-2 text-sm font-semibold text-brand-blue hover:bg-brand-gray/50 hover:text-brand-maroon"
                >
                  OBA Projects
                </Link>
                <Link
                  to="/projects/other"
                  role="menuitem"
                  className="block px-4 py-2 text-sm font-semibold text-brand-blue hover:bg-brand-gray/50 hover:text-brand-maroon"
                >
                  Affiliated Projects
                </Link>
              </div>
            ) : null}
          </div>

          <div className="relative" ref={newsWrapRef}>
            <button
              type="button"
              aria-expanded={newsOpen}
              aria-haspopup="true"
              onClick={() => setNewsOpen((o) => !o)}
              className={[
                "inline-flex items-center gap-1 text-sm font-semibold tracking-wide uppercase transition-colors",
                newsActive || newsOpen
                  ? "text-brand-maroon"
                  : "text-brand-blue hover:text-brand-maroon",
              ].join(" ")}
            >
              News
              <span className="text-xs opacity-80" aria-hidden>
                {newsOpen ? "▴" : "▾"}
              </span>
            </button>
            {newsOpen ? (
              <div
                className="absolute left-0 top-full z-50 mt-1 min-w-[14rem] rounded-lg border border-brand-gray bg-white py-1 shadow-lg"
                role="menu"
              >
                <Link
                  to="/news"
                  role="menuitem"
                  className="block px-4 py-2 text-sm font-semibold text-brand-blue hover:bg-brand-gray/50 hover:text-brand-maroon"
                >
                  OBA News
                </Link>
                <Link
                  to="/news/other"
                  role="menuitem"
                  className="block px-4 py-2 text-sm font-semibold text-brand-blue hover:bg-brand-gray/50 hover:text-brand-maroon"
                >
                  Affiliated News
                </Link>
              </div>
            ) : null}
          </div>

          <NavLink to="/contact" className={navClass}>
            Contact
          </NavLink>
        </nav>
      </div>
    </header>
  );
}
